import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Box,
} from '@mui/material';
import { IDiffFeature, IDiffResponse, IDiffValue } from '../../../types';
import { DiffColumns } from '../../data/Constants';
import { useTranslation } from 'react-i18next';

function getAllPropertyCodes(feature: { [key: string]: IDiffFeature }) {
  const fromCodes = feature?.from?.properties?.map((property: IDiffValue) => property.code) ?? [];
  const toCodes = feature?.to?.properties?.map((property: IDiffValue) => property.code) ?? [];
  return [...fromCodes, ...toCodes];
}

function getAllOptionCodes(feature: { [key: string]: IDiffFeature }) {
  const fromCodes = feature?.from?.options?.map((option: IDiffValue) => option.code) ?? [];
  const toCodes = feature?.to?.options?.map((option: IDiffValue) => option.code) ?? [];
  return [...fromCodes, ...toCodes];
}

function renderPropertyChanges(feature: { [key: string]: IDiffFeature }) {
  return getAllPropertyCodes(feature).map((code) => {
    const oldVal = feature?.from?.properties?.find(
      (property: IDiffValue) => property.code === code
    )?.value;
    const newVal = feature?.to?.properties?.find(
      (property: IDiffValue) => property.code === code
    )?.value;
    if (oldVal !== newVal) {
      return (
        <TableRow key={code}>
          <TableCell>{code}</TableCell>
          <TableCell>{String(oldVal)}</TableCell>
          <TableCell>{String(newVal)}</TableCell>
        </TableRow>
      );
    }
    return null;
  });
}

function renderOptionChanges(feature: { [key: string]: IDiffFeature }) {
  return getAllOptionCodes(feature).map((code) => {
    const oldVal = feature?.from?.options?.find(
      (option: IDiffValue) => option.code === code
    )?.value;
    const newVal = feature?.to?.options?.find((option: IDiffValue) => option.code === code)?.value;
    if (oldVal !== newVal) {
      return (
        <TableRow key={code}>
          <TableCell>{code}</TableCell>
          <TableCell>{oldVal ?? '-'}</TableCell>
          <TableCell>{newVal ?? '-'}</TableCell>
        </TableRow>
      );
    }
    return null;
  });
}

function getUpdatedFeature(diffResponse: IDiffResponse, featureCode: string) {
  return diffResponse?.updated?.find(
    (value: { [key: string]: IDiffFeature }) =>
      value?.from?.code === featureCode || value?.to?.code === featureCode
  );
}

/**
 * This component shows the table with differences for the options and properties
 */
const DiffInfoTable = ({ diffResponse, featureCode }) => {
  const { t } = useTranslation();
  const feature = getUpdatedFeature(diffResponse, featureCode);

  if (!feature) {
    return <Box className="justify-center">No diffrence found</Box>;
  }

  return (
    <Box>
      <Typography className="diff-info-header">
        {feature.to?.value ?? feature.from?.value} ({featureCode})
      </Typography>

      {/* Property Changes */}
      {getAllPropertyCodes(feature).length > 0 && (
        <>
          <Typography className="diff-info-header">{t('label.properties')}</Typography>
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  {DiffColumns.property.map((column) => {
                    return (
                      <TableCell key={column.field}>
                        <strong>{t(`label.${column.field}`)}</strong>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>{renderPropertyChanges(feature)}</TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Option Changes */}
      {getAllOptionCodes(feature).length > 0 && (
        <>
          <Typography className="diff-info-header"> {t('label.options')}</Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {DiffColumns.option.map((column) => {
                    return (
                      <TableCell key={column.field}>
                        <strong>{t(`label.${column.field}`)}</strong>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>{renderOptionChanges(feature)}</TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      <Divider sx={{ mt: 3, mb: 3 }} />
    </Box>
  );
};

/**
 * This component renders the table for show the differences for feature and options
 */
const DiffInfDialog = ({ open, diffResponse, featureCode, handCloseDialog }) => {
  return (
    <Dialog
      open={open}
      className="common-dialog-style infoDialogStyle"
      onClose={handCloseDialog}
      slotProps={{ paper: { className: 'paper' } }}
      data-testid="diff-info-dialog"
    >
      <DialogTitle className="header title">Diff details</DialogTitle>
      <DialogContent>
        <DiffInfoTable diffResponse={diffResponse} featureCode={featureCode} />
      </DialogContent>
      <DialogActions>
        <Button onClick={handCloseDialog}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DiffInfDialog;
